import { BucketServiceAttachmentSocketEventDto } from '@nimbus/bucket-service';
import {
  DuplicateFolderState,
  DuplicateFolderStatus,
  UpdateDuplicateFolderStatePayload,
  ITEMS_TO_SHOW_DUPLICATION_PROGRESS,
} from '@nx-frontend/shared/store/duplicate-folder';
import { BucketFile, FileType } from '@nx-frontend/shared/types';
import { clientGetBucketGroupInclusions } from '@nx-frontend/webnotes-client/shared/api';
import { AddWorkspaceFilePayload } from '@nx-frontend/webnotes-client/shared/store';
import { getStaticFileUrl, getNoteStaticFileUrl } from '@nx-frontend/webnotes-client/shared/utils';

interface SocketData<T> {
  message: T;
  workspaceId: string;
  userId: number;
}

interface BucketsAttachmentCreatedMessage {
  userId: number;
  workspaceId: string;
  before: BucketServiceAttachmentSocketEventDto | null;
  after: BucketServiceAttachmentSocketEventDto | null;
}

interface NoteCopyEventPayload {
  removeOriginal: boolean;
  from: {
    noteGlobalId: string;
    workspaceId: string;
  };
  to: {
    noteGlobalId: string;
    workspaceId: string;
  };
  welcomeNote: boolean;
  isDefaultWelcomeNote: boolean;
}

interface AppSocketEventsListenerParams {
  workspaceId: string;
  showPortalFiles?: boolean;
  listenSocketEvent: <T>(eventName: string, listener: (data: SocketData<T>) => void) => void;
  upsertFile: (file: BucketFile) => void;
  addWorkspaceFile: (data: AddWorkspaceFilePayload) => void;
  updateDuplicateFolderState: (payload: UpdateDuplicateFolderStatePayload) => void;
  getDuplicateFolderState: () => Promise<DuplicateFolderState>;
  getAiAgentCategoriesDebounced: () => void;
}

export class AppSocketEventsListener {
  static instance: AppSocketEventsListener;
  private params: AppSocketEventsListenerParams | null = null;
  private inited = false;

  static getInstance() {
    if (!this.instance) {
      this.instance = new AppSocketEventsListener();
    }
    return this.instance;
  }

  public init = (params: AppSocketEventsListenerParams) => {
    this.params = params;

    if (!this.inited) {
      this.inited = true;
      this.listen();
    }
  };

  private listen = () => {
    const {
      showPortalFiles,
      listenSocketEvent,
      addWorkspaceFile,
      upsertFile,
      updateDuplicateFolderState,
      getDuplicateFolderState,
      getAiAgentCategoriesDebounced,
    } = this.params || {};

    if (!listenSocketEvent) return;

    listenSocketEvent<BucketsAttachmentCreatedMessage>(
      'buckets:attachmentCreated',
      async ({ message, workspaceId }) => {
        const file = message.after;
        if (file) {
          const url =
            file.noteServiceAttachment && file.noteId
              ? getNoteStaticFileUrl(file.workspaceId, file.noteId, file.storedFileUUID, file.filename)
              : getStaticFileUrl(file.storedFileUUID, file.filename);

          if (!showPortalFiles) {
            // request to groupinclusion to check access to file
            const groups = await clientGetBucketGroupInclusions(file.bucketId);
            if (groups.find((group) => group.target === 'portal')) {
              file.deleted = true;
            }
          }

          const bucketFile: BucketFile = {
            globalId: file.globalId,
            bucketId: file.bucketId,
            target: file.noteServiceAttachment ? 'note' : '',
            targetId: file.noteId ? file.noteId : '',
            workspaceId: workspaceId || file.workspaceId,
            filename: file.filename,
            type: file.kind as FileType,
            userId: file.userId,
            size: file.size,
            createdAt: (file.createdAt ?? 0) * 1000,
            deleted: file.deleted,
            isNew: true,
            url,
          };

          addWorkspaceFile &&
            addWorkspaceFile({
              workspaceId: workspaceId || file.workspaceId,
              file: bucketFile,
            });

          !file.deleted && upsertFile && upsertFile(bucketFile);
        }
      }
    );

    listenSocketEvent<{ taskId: string; total: number; current: number }>(
      'note:copyProgress',
      async ({ message, workspaceId }) => {
        let state: DuplicateFolderState | null = null;
        if (getDuplicateFolderState) {
          state = await getDuplicateFolderState();
        }
        if (state?.taskId === message.taskId && updateDuplicateFolderState) {
          const { total, current } = message;
          if (total > ITEMS_TO_SHOW_DUPLICATION_PROGRESS) {
            state.TID && clearTimeout(state.TID);
            updateDuplicateFolderState({
              total,
              current,
              status: DuplicateFolderStatus.PROGRESS,
            });
            if (total === current && this.params?.workspaceId !== workspaceId) {
              updateDuplicateFolderState({
                jobId: '',
                taskId: '',
                status: DuplicateFolderStatus.COMPLETED,
              });
              const TID = setTimeout(() => {
                updateDuplicateFolderState({
                  status: DuplicateFolderStatus.NONE,
                });
              }, 10 * 1000);
              updateDuplicateFolderState({ TID });
            }
          } else {
            updateDuplicateFolderState({
              jobId: '',
              status: DuplicateFolderStatus.NONE,
            });
          }
        }
      }
    );

    listenSocketEvent<NoteCopyEventPayload>('note:copy', async ({ message }) => {
      let state: DuplicateFolderState | null = null;
      if (getDuplicateFolderState) {
        state = await getDuplicateFolderState();
      }
      if (
        message.from.noteGlobalId === state?.globalId &&
        [DuplicateFolderStatus.COMPLETED, DuplicateFolderStatus.PROGRESS].includes(state.status)
      ) {
        updateDuplicateFolderState &&
          updateDuplicateFolderState({
            copyGlobalId: message.to.noteGlobalId,
          });
      }
    });

    listenSocketEvent<{ agentId: string; categories: string[] }>('aiAgents:updateCategories', async () => {
      getAiAgentCategoriesDebounced?.();
    });

    listenSocketEvent<{ agentId: string; categories: string[] }>('aiAgents:delete', async () => {
      getAiAgentCategoriesDebounced?.();
    });
  };
}
